<script lang="ts" setup>
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

const props = withDefaults(
    defineProps<{
        condensed?: boolean;
        items?: any[] | Record<string, any>;
        loop?: boolean;
        small?: boolean;
        grid?: string;
        cols?: string;
        noContainer?: boolean;
        padding?: true | string;
        leftSpan?: string;
        rightSpan?: string;
        ui?: string;
        hideEmpty?: boolean;
        except?: string[];
        class?: string;
        gap?: string;
        paddingClass?: string;
        paddingClassCondensed?: string;
        titleClass?: any;
        listClass?: any;
        placement?: "start" | "center";
        subClass?: string;
        itemPadding?: string;
        divideColor?: string;
    }>(),
    {
        placement: "center",
        padding: true,
        small: false,
        paddingClass: "p-4",
        paddingClassCondensed: "px-4 py-3",
        items: () => [],
        except: () => [],
        grid: undefined,
        cols: undefined,
        leftSpan: undefined,
        divideColor: "divide-gray-100",
        rightSpan: undefined,
        ui: undefined,
        hideEmpty: undefined,
        gap: undefined,
        class: undefined,
        subClass: undefined,
        itemPadding: undefined,
    },
);

function isLabelValuePair(item: any) {
    return (
        item && typeof item === "object" && "label" in item && "value" in item
    );
}

function hasItems(item: any) {
    return (
        item &&
        typeof item === "object" &&
        "value" in item &&
        Array.isArray(item.value)
    );
}

function itemonly(item: any) {
    return item && typeof item === "object" && item.item_only;
}

function isEmpty(value: any) {
    return (
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "object" && Object.keys(value).length === 0)
    );
}
</script>
<template>
    <div
        class="divide-y divide-gray-100"
        :class="[ui, props.class, { '@container': !noContainer }]"
    >
        <div
            v-if="$slots.title || $slots.description"
            class="flex"
            :class="[condensed ? paddingClassCondensed : paddingClass, titleClass]"
        >
            <div class="flex-1">
                <h3
                    v-if="$slots.title"
                    class="text-base font-medium leading-7 text-gray-900 font-display"
                >
                    <slot name="title" />
                </h3>
                <p
                    v-if="$slots.description"
                    class="mt-1 max-w-2xl text-sm leading-6 text-gray-500"
                >
                    <slot name="description" />
                </p>
            </div>
            <slot name="action" />
        </div>
        <div
            v-if="
                $slots.default ||
                (items &&
                    (items.length ||
                        (typeof items === 'object' &&
                            !Array.isArray(items) &&
                            Object.keys(items).length)))
            "
            :class="listClass"
        >
            <dl class="divide-y" :class="divideColor">
                <slot 
                    :condensed="condensed"
                    :grid="grid"
                    :cols="cols"
                    :placement="placement"
                    :small="small"
                    :gap="gap"
                    :padding="padding"
                    :item-padding="itemPadding"
                    :left-span="leftSpan"
                    :right-span="rightSpan"
                />
                <template v-for="(item, index) in items" :key="index">
                    <LazyUIDataListItem
                        v-if="
                            (!hideEmpty || !isEmpty(item)) &&
                            !except.includes(index)
                        "
                        :condensed="condensed"
                        :grid="grid"
                        :cols="cols"
                        :placement="placement"
                        :small="small"
                        :gap="gap"
                        :padding="padding"
                        :item-padding="itemPadding"
                        :left-span="leftSpan"
                        :right-span="rightSpan"
                    >
                        <template
                            v-if="
                                !itemonly(item) &&
                                ($slots.label ||
                                    (item &&
                                        typeof item === 'object' &&
                                        'label' in item))
                            "
                            #label
                        >
                            <slot
                                name="label"
                                :item="item"
                                :condensed="condensed"
                                :index="index"
                            >
                                {{
                                    item &&
                                    typeof item === "object" &&
                                    "label" in item
                                        ? item.label
                                        : undefined
                                }}
                                <span
                                    v-if="
                                        item &&
                                        typeof item === 'object' &&
                                        'tooltip' in item
                                    "
                                    class="relative ml-1 flex items-center"
                                >
                                    <UTooltip
                                        :text="item.tooltip"
                                        :popper="{
                                            placement: 'right',
                                            strategy: 'absolute',
                                        }"
                                    >
                                        <span
                                            ><FontAwesomeIcon
                                                :icon="faCircleInfo"
                                        /></span>
                                    </UTooltip>
                                </span>
                            </slot>
                        </template>
                        <template
                            v-if="
                                !itemonly(item) &&
                                ($slots.value ||
                                    (item &&
                                        typeof item === 'object' &&
                                        'value' in item))
                            "
                            #value
                        >
                            <template
                                v-if="
                                    loop &&
                                    (!isLabelValuePair(item) ||
                                        hasItems(item)) &&
                                    typeof item === 'object' &&
                                    item
                                "
                            >
                                <UIDataList
                                    :loop="loop"
                                    :condensed="condensed"
                                    :items="
                                        hasItems(item)
                                            ? item.value
                                            : Array.isArray(item) &&
                                                item.length === 1 &&
                                                typeof item[0] === 'object'
                                              ? item[0]
                                              : item
                                    "
                                    :grid="grid"
                                    :cols="cols"
                                    :class="subClass ?? props.class"
                                    :sub-class="subClass"
                                    :padding="padding"
                                    :except="except"
                                    :item-padding="itemPadding"
                                    :gap="gap"
                                    :hide-empty="hideEmpty"
                                    :placement="placement"
                                    :left-span="leftSpan"
                                    :right-span="rightSpan"
                                    :divide-color="divideColor"
                                    :ui="ui"
                                >
                                    <template
                                        v-if="$slots.value"
                                        #value="{ index: idx, item: itm }"
                                    >
                                        <slot
                                            name="value"
                                            :item="itm"
                                            :condensed="condensed"
                                            :index="idx"
                                        />
                                    </template>
                                    <template
                                        v-if="$slots.label"
                                        #label="{ index: idx, item: itm }"
                                    >
                                        <slot
                                            name="label"
                                            :item="itm"
                                            :condensed="condensed"
                                            :index="idx"
                                        />
                                    </template>
                                </UIDataList>
                            </template>
                            <template v-else>
                                <slot
                                    name="value"
                                    :item="item"
                                    :condensed="condensed"
                                    :index="index"
                                >
                                    {{
                                        item &&
                                        typeof item === "object" &&
                                        "value" in item
                                            ? item.value
                                            : undefined
                                    }}
                                </slot>
                            </template>
                        </template>
                        <template v-if="$slots.item" #default>
                            <slot
                                name="item"
                                :item="item"
                                :condensed="condensed"
                                :index="index"
                            />
                        </template>
                    </LazyUIDataListItem>
                </template>
                <slot 
                    name="end" 
                    :condensed="condensed"
                    :grid="grid"
                    :cols="cols"
                    :placement="placement"
                    :small="small"
                    :gap="gap"
                    :padding="padding"
                    :item-padding="itemPadding"
                    :left-span="leftSpan"
                    :right-span="rightSpan"
                />
            </dl>
        </div>
        <div v-if="$slots.footer" :class="condensed ? 'px-4 py-3' : 'p-4'">
            <slot name="footer" />
        </div>
    </div>
</template>
